import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { antdMessage } from '@hellodarwin/core/lib/features/providers/antd-app-provider';
import Div from '@hellodarwin/core/lib/components/common/div';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import Loading from '@hellodarwin/core/lib/components/loading';
import { InitialGinStepApplicationItem, } from '@hellodarwin/core/lib/features/entities/gin-step-entities';
import { useCompanySize } from '@hellodarwin/core/lib/features/helpers/company-infos';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import theme from '@hellodarwin/core/lib/theme';
import Enterprise from '@hellodarwin/icons/dist/icons/Enterprise';
import Invoices from '@hellodarwin/icons/dist/icons/Invoices';
import DatePicker from 'antd/es/date-picker';
import Empty from 'antd/es/empty';
import Input from 'antd/es/input';
import Select from 'antd/es/select';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useGinSteps } from '../../../gin-steps-provider';
import { companyColumnTranslationKeysByColumn, programColumnTranslationKeysByColumn, } from '../../../types';
import { optionFromRawPropertyValue } from '../../../utils';
import { getItemColor, } from '../gin-step-item-content-selector';
import GinStepPropertyConditions from './gin-step-property-conditions';
const GinStepPropertyContent = ({ itemId, isRequired, item, isDisabled, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
    const { t } = useTranslations();
    const { selectedLocale } = useLocale();
    const [cancelNextBlur, setCancelNextBlur] = useState(false);
    const { formState, formFillingProps, formViewingProps } = useGinSteps();
    const companySizeOptions = useCompanySize();
    const applicationItemState = formFillingProps
        ? formFillingProps.applicationItemStatesById.get(itemId)
        : formViewingProps === null || formViewingProps === void 0 ? void 0 : formViewingProps.applicationItemStatesById.get(itemId);
    const ginStepItemApplication = applicationItemState === null || applicationItemState === void 0 ? void 0 : applicationItemState.ginStepItemApplication;
    const isLoading = (_a = applicationItemState === null || applicationItemState === void 0 ? void 0 : applicationItemState.isLoading) !== null && _a !== void 0 ? _a : false;
    const options = useMemo(() => {
        var _a;
        const col = (_a = item.property) === null || _a === void 0 ? void 0 : _a.property_column;
        switch (col) {
            case 'size':
                return companySizeOptions === null || companySizeOptions === void 0 ? void 0 : companySizeOptions.map((o) => ({
                    label: o.label,
                    value: o.value,
                }));
            case 'naics_code':
                return formFillingProps === null || formFillingProps === void 0 ? void 0 : formFillingProps.propertyItemProps.naicsCodes;
            case 'industry_sector':
                return formFillingProps === null || formFillingProps === void 0 ? void 0 : formFillingProps.propertyItemProps.industrySectors;
            case 'industry_subsector':
                return formFillingProps === null || formFillingProps === void 0 ? void 0 : formFillingProps.propertyItemProps.industrySubsectors;
            case 'province':
                return formFillingProps === null || formFillingProps === void 0 ? void 0 : formFillingProps.propertyItemProps.provinces;
            default:
                return undefined;
        }
    }, [
        (_b = item.property) === null || _b === void 0 ? void 0 : _b.property_column,
        companySizeOptions,
        formFillingProps === null || formFillingProps === void 0 ? void 0 : formFillingProps.propertyItemProps,
    ]);
    const rawValue = (_c = ginStepItemApplication === null || ginStepItemApplication === void 0 ? void 0 : ginStepItemApplication.property_application) === null || _c === void 0 ? void 0 : _c.value;
    const selectedOption = useMemo(() => {
        var _a, _b;
        return optionFromRawPropertyValue((_a = item.property) === null || _a === void 0 ? void 0 : _a.property_column, (_b = item.property) === null || _b === void 0 ? void 0 : _b.property_type, rawValue, selectedLocale, options);
    }, [
        (_d = item.property) === null || _d === void 0 ? void 0 : _d.property_column,
        (_e = item.property) === null || _e === void 0 ? void 0 : _e.property_type,
        rawValue,
        selectedLocale,
        options,
    ]);
    const isSameValue = (newVal) => {
        const prev = selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value;
        if (prev === undefined || prev === null)
            return newVal === '';
        return String(prev) === String(newVal);
    };
    const propertyType = useMemo(() => {
        const prop = item.property;
        if (!prop)
            return undefined;
        if (prop.property_column === 'business_number' ||
            prop.property_column === 'city' ||
            prop.property_column === 'cra_number') {
            return 'string';
        }
        if (prop.property_type === 'string' || prop.property_column === 'size') {
            return 'options';
        }
        return prop.property_type;
    }, [item.property]);
    const propertyLabel = useMemo(() => {
        if (!item.property)
            return '';
        const { property_table, property_column, property_label } = item.property;
        if (property_table === 'company') {
            if (!property_column)
                return property_label;
            const translationKey = companyColumnTranslationKeysByColumn.get(property_column);
            return translationKey ? t(translationKey) : property_label;
        }
        if (property_table === 'program') {
            if (!property_column)
                return property_label;
            const translationKey = programColumnTranslationKeysByColumn.get(property_column);
            return translationKey ? t(translationKey) : property_label;
        }
        return property_label;
    }, [item.property, t]);
    const handlePropertySave = async (value) => {
        if (!(formFillingProps === null || formFillingProps === void 0 ? void 0 : formFillingProps.propertyItemProps))
            return;
        try {
            const lightGinStepItem = Object.assign(Object.assign({}, item), { application_item: undefined });
            const form = await formFillingProps.propertyItemProps.updateGinStepProperty(value, lightGinStepItem, ginStepItemApplication || InitialGinStepApplicationItem);
            if (form.program_status_trigger) {
                formFillingProps.triggerProgramStatusDialog(form.program_status_trigger);
            }
        }
        catch (_) {
            antdMessage.error('Failed to update property');
        }
    };
    if (!(item === null || item === void 0 ? void 0 : item.property))
        return null;
    if (!item.property.property_label ||
        !item.property.property_type ||
        !item.property.property_column) {
        return (_jsx(Empty, { description: _jsx(Typography, { elementTheme: "subtitle2", color: theme.colors.grey_2, children: t('gin_steps|no_content') }) }));
    }
    return (_jsxs(Div, { flex: "column", gap: 8, children: [_jsxs(Div, { style: {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                }, children: [_jsx(Div, { style: {
                            width: 20,
                            height: 20,
                            minWidth: 20,
                            minHeight: 20,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: '0 0 20px',
                        }, children: item.property.property_table === 'company' ? (_jsx(Enterprise, { size: 20, color: getItemColor(formState, theme, (_f = ginStepItemApplication === null || ginStepItemApplication === void 0 ? void 0 : ginStepItemApplication.property_application) === null || _f === void 0 ? void 0 : _f.valid) })) : (_jsx(Invoices, { size: 20, color: getItemColor(formState, theme, (_g = ginStepItemApplication === null || ginStepItemApplication === void 0 ? void 0 : ginStepItemApplication.property_application) === null || _g === void 0 ? void 0 : _g.valid) })) }), _jsxs(Div, { fitContent: true, style: { display: 'flex', alignItems: 'center', gap: 1 }, children: [isRequired && (_jsx(Typography, { elementTheme: "body3", color: theme.colors.red_1, style: {
                                    fontFamily: 'Inter',
                                    fontWeight: 700,
                                    fontSize: 14,
                                }, children: "*" })), item.property.property_column && (_jsx(Typography, { elementTheme: "body2", color: getItemColor(formState, theme, (_h = ginStepItemApplication === null || ginStepItemApplication === void 0 ? void 0 : ginStepItemApplication.property_application) === null || _h === void 0 ? void 0 : _h.valid), style: {
                                    fontWeight: 700,
                                    minWidth: 0,
                                    maxWidth: '100%',
                                }, children: propertyLabel }))] }), _jsx(Div, { style: {
                            flex: 1,
                            borderBottom: `1px dotted ${getItemColor(formState, theme, (_j = ginStepItemApplication === null || ginStepItemApplication === void 0 ? void 0 : ginStepItemApplication.property_application) === null || _j === void 0 ? void 0 : _j.valid)}`,
                            position: 'relative',
                            top: '8px',
                        } }), (formState === 'filling' || formState === 'viewing') &&
                        (isLoading ? (_jsx(Div, { style: { width: 'fit-content' }, children: _jsx(Loading, { size: "small" }) })) : (_jsxs(Div, { style: { width: 'fit-content', maxWidth: '60%' }, children: [propertyType === 'string' && (_jsx(Input, { disabled: isDisabled, defaultValue: (_k = selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value) !== null && _k !== void 0 ? _k : '', size: "small", placeholder: propertyLabel, onKeyDown: (e) => {
                                        if (e.key === 'Escape') {
                                            setCancelNextBlur(true);
                                        }
                                    }, onPressEnter: (e) => e.currentTarget.blur(), onBlur: (e) => {
                                        var _a;
                                        if (cancelNextBlur) {
                                            setCancelNextBlur(false);
                                            return;
                                        }
                                        const nextVal = String((_a = e.currentTarget.value) !== null && _a !== void 0 ? _a : '');
                                        if (isSameValue(nextVal)) {
                                            return;
                                        }
                                        handlePropertySave(nextVal);
                                    } })), propertyType === 'number' && (_jsx(Input, { disabled: isDisabled, type: "number", size: "small", placeholder: propertyLabel, defaultValue: typeof (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value) === 'number'
                                        ? selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value
                                        : (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value)
                                            ? String(selectedOption.value)
                                            : '', onKeyDown: (e) => {
                                        if (e.key === 'Escape') {
                                            setCancelNextBlur(true);
                                        }
                                    }, onPressEnter: (e) => e.currentTarget.blur(), onBlur: (e) => {
                                        var _a;
                                        if (cancelNextBlur) {
                                            setCancelNextBlur(false);
                                            return;
                                        }
                                        const nextVal = String((_a = e.currentTarget.value) !== null && _a !== void 0 ? _a : '');
                                        if (isSameValue(nextVal)) {
                                            return;
                                        }
                                        handlePropertySave(nextVal);
                                    } })), propertyType === 'date' && (_jsx(DatePicker, { format: "YYYY-MM-DD", placeholder: propertyLabel, defaultValue: (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value) &&
                                        selectedOption.value instanceof Date
                                        ? dayjs(selectedOption.value)
                                        : undefined, onChange: (val) => {
                                        if (val) {
                                            handlePropertySave(val.format('YYYY-MM-DD'));
                                        }
                                    } })), propertyType === 'options' && (_jsx(Select, { disabled: isDisabled, style: {
                                        maxWidth: '100%',
                                    }, showSearch: true, placeholder: propertyLabel, defaultValue: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value, options: options === null || options === void 0 ? void 0 : options.map((o) => ({
                                        label: o.label,
                                        value: o.value,
                                    })), onChange: (val) => handlePropertySave(val !== null && val !== void 0 ? val : ''), popupMatchSelectWidth: false })), propertyType === 'string-set' && (_jsx(Select, { disabled: isDisabled, style: {
                                        maxWidth: '100%',
                                    }, mode: "multiple", placeholder: propertyLabel, defaultValue: (_l = selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value) !== null && _l !== void 0 ? _l : [], options: options === null || options === void 0 ? void 0 : options.map((o) => ({
                                        label: (_jsx("div", { style: {
                                                maxWidth: '400px',
                                                whiteSpace: 'normal',
                                                wordWrap: 'break-word',
                                            }, children: o.label })),
                                        value: o.value,
                                    })), onChange: (vals) => handlePropertySave(vals), popupMatchSelectWidth: false, filterOption: (input, option) => {
                                        var _a, _b, _c, _d;
                                        return (_d = (_c = (_b = (_a = option === null || option === void 0 ? void 0 : option.label) === null || _a === void 0 ? void 0 : _a.props) === null || _b === void 0 ? void 0 : _b.children) === null || _c === void 0 ? void 0 : _c.toLowerCase().includes(input.toLowerCase())) !== null && _d !== void 0 ? _d : false;
                                    } }))] })))] }), (((formState === 'filling' || formState === 'viewing') &&
                !((_o = (_m = item.application_item) === null || _m === void 0 ? void 0 : _m.property_application) === null || _o === void 0 ? void 0 : _o.valid)) ||
                formState === 'building') && (_jsx(GinStepPropertyConditions, { conditions: item.conditions, isDateProperty: ((_p = item.property) === null || _p === void 0 ? void 0 : _p.property_type) === 'date', isNumberProperty: ((_q = item.property) === null || _q === void 0 ? void 0 : _q.property_type) === 'number', selectedLocale: selectedLocale, accentColor: formState === 'building' ? theme.colors.grey_1 : theme.colors.red_1, propertyLabel: propertyLabel !== null && propertyLabel !== void 0 ? propertyLabel : '' }))] }));
};
export default GinStepPropertyContent;
